
import { useState, useRef, useEffect } from "react"
import {
  PurposeCodeOption,
  getMccsForPurposeCode,
  selectAllMccsInPurposeCode,
  deselectAllMccsInPurposeCode,
  areAllMccsInPurposeCodeSelected,
  toggleMcc,
} from "../../utils/mccSelection"
import { LOCATION_STATES, getCitiesForState } from "../../data/locationCodes"
import { derivePeriodFromMypExpiry } from "../../utils/mypExpiry"

// Maps a processorType string (from the processor dropdown, sourced from
// the `processors` table) to the real-world brand it identifies. Tolerates
// both the pre-consolidation lifecycle-suffixed values (e.g. 'ICICI_DIRECT',
// 'ICICI_INDIRECT', 'ICICI_PILOT') and the post-consolidation bare-brand
// values ('ICICI') - mirrors vms's processor.service.js#normalizeBrand.
// ICICI CBDC is checked before the plain ICICI prefix since it also starts
// with "ICICI" but is a distinct product/brand, not an ICICI lifecycle
// variant.
export type ProcessorBrand = 'ICICI' | 'ICICI_CBDC' | 'YESBANK' | 'SELP' | 'BLOCKCHAIN' | 'MYP' | null

export const getProcessorBrand = (processorType: string): ProcessorBrand => {
  const t = (processorType || '').toUpperCase()
  if (t.startsWith('ICICI_CBDC')) return 'ICICI_CBDC'
  if (t.startsWith('ICICI')) return 'ICICI'
  if (t.startsWith('YESBANK')) return 'YESBANK'
  if (t === 'SELP') return 'SELP'
  if (t === 'BLOCKCHAIN') return 'BLOCKCHAIN'
  if (t === 'MYP') return 'MYP'
  return null
}

// Validation functions for each processor
export const validateBankDetails = (processorType: string, formData: any, errors: Record<string, string>) => {
  let hasError = false



  // MYP has no Period input - period is derived from the expiry, which is validated below.
  if (!formData.period && getProcessorBrand(processorType) !== 'MYP') {
    hasError = true
    errors.period = "Please enter a valid period"
  }

  // Validate processor-specific bank details
  switch (getProcessorBrand(processorType)) {
    case 'ICICI':

      // Validate common bank & redemption details for all processors that require them
      if (!formData.redemptionType) {
        hasError = true
        errors.redemptionType = "Please select a redemption type"
      }

      if (!formData.merchantId) {
        hasError = true
        errors.merchantId = "Please enter a merchant ID"
      } else {
        // Filter out non-digits and update the form data
        const filteredMerchantId = String(formData.merchantId).replace(/\D/g, "")
        if (filteredMerchantId === "") {
          hasError = true
          errors.merchantId = "Merchant ID cannot be empty"
        }
      }

      if (!formData.subMerchantId) {
        hasError = true
        errors.subMerchantId = "Please enter a sub-merchant ID"
      } else {
        // Filter out non-digits and update the form data
        const filteredSubMerchantId = String(formData.subMerchantId).replace(/\D/g, "")
        if (filteredSubMerchantId === "") {
          hasError = true
          errors.subMerchantId = "Sub-merchant ID cannot be empty"
        }
      }

      // Common Payer VA validation
      if (!formData.bankDetails?.payerVa) {
        hasError = true
        errors.bankDetailsPayerVa = "Please enter payer VA"
      }
      // MCC validation
      if (!formData.bankDetails?.mcc) {
        hasError = true
        errors.bankDetailsMcc = "Please select MCC"
      }
      // Purpose Code validation
      if (!formData.bankDetails?.purposeCode) {
        hasError = true
        errors.bankDetailsPurposeCode = "Please select purpose code"
      }
      // Mandate Type validation
      if (!formData.bankDetails?.mandateType) {
        hasError = true
        errors.bankDetailsMandateType = "Please select mandate type"
      }
      // Payee VPA validation for Closed Loop
      if (formData.bankDetails?.mandateType === "04" && !formData.bankDetails?.payeeVpa) {
        hasError = true
        errors.bankDetailsPayeeVpa = "Please enter payee VPA for closed loop"
      }
      // Access Key validation
      if (!formData.bankDetails?.accessKey) {
        hasError = true
        errors.bankDetailsAccessKey = "Please enter access key"
      }
      break

    case 'YESBANK':

      // Purpose Code validation
      if (!formData.bankDetails?.purposeCode) {
        hasError = true
        errors.bankDetailsPurposeCode = "Please select purpose code"
      }
      // Mandate Name validation
      if (!formData.bankDetails?.mandateName || (formData.bankDetails.mandateName || "").length > 50) {
        hasError = true
        errors.bankDetailsMandateName = "Please enter mandate name (max 50 chars)"
      }
      // Amount Rule validation
      if (!formData.bankDetails?.amountRule || !["MAX", "EXACT"].includes(String(formData.bankDetails.amountRule))) {
        hasError = true
        errors.bankDetailsAmountRule = "Please select a valid amount rule"
      }
      // Voucher Category validation
      if (!formData.bankDetails?.voucherCategory || !["CORPORATE", "GOVT"].includes(String(formData.bankDetails.voucherCategory))) {
        hasError = true
        errors.bankDetailsVoucherCategory = "Please select voucher category"
      }
      // Recurrence Pattern validation
      if (!formData.bankDetails?.recurrencePattern || !["ONETIME", "ASPRESENTED", "RELOADABLE"].includes(String(formData.bankDetails.recurrencePattern))) {
        hasError = true
        errors.bankDetailsRecurrencePattern = "Please select recurrence pattern"
      }
      // Payer Revocable validation
      if (!formData.bankDetails?.payerRevocable || !["TRUE", "FALSE"].includes(String(formData.bankDetails.payerRevocable))) {
        hasError = true
        errors.bankDetailsPayerRevocable = "Please choose payer revocable"
      }
      // Pre Auth validation (required)
      if (formData.bankDetails?.preAuth === undefined || formData.bankDetails?.preAuth === null) {
        hasError = true
        errors.bankDetailsPreAuth = "Please select pre auth"
      }
      // Ref URL validation (optional but must be valid URL if provided)
      if (formData.bankDetails?.refurl) {
        try {
          new URL(formData.bankDetails.refurl)
        } catch {
          hasError = true
          errors.bankDetailsRefurl = "Please enter a valid URL (e.g. https://example.com)"
        }
      }
      break

    case 'ICICI_CBDC':
      // ICICI CBDC validation is being rebuilt fresh against the real
      // Create/Update Rule API spec (sponsor-details, purpose, mcc,
      // location, language, amount) - see task tracked separately.
      // No validation is applied for now, per explicit instruction not to
      // validate anywhere until the confirmed spec's fields are wired in.
      break

    case 'MYP':
      if (!formData.bankDetails?.expiry?.mode) {
        hasError = true
        errors.bankDetailsExpiryMode = "Please select an expiry mode"
      } else if (formData.bankDetails.expiry.mode === 'DAYS_FROM_LOAD' && !formData.bankDetails.expiry.days) {
        hasError = true
        errors.bankDetailsExpiryDays = "Please enter the number of days"
      } else if (formData.bankDetails.expiry.mode === 'FIXED_DATE' && !formData.bankDetails.expiry.date) {
        hasError = true
        errors.bankDetailsExpiryDate = "Please select an expiry date"
      }
      if (!formData.bankDetails?.limits?.maxLoad) {
        hasError = true
        errors.bankDetailsMaxLoad = "Please enter a max load"
      }
      if (!formData.bankDetails?.limits?.maxBalance) {
        hasError = true
        errors.bankDetailsMaxBalance = "Please enter a max balance"
      }
      if (formData.bankDetails?.merchantRules?.mode === 'RESTRICTED') {
        const mccs = (formData.bankDetails?.merchantRules?.mccs || '').trim()
        const merchantIds = (formData.bankDetails?.merchantRules?.merchantIds || '').trim()
        if (!mccs && !merchantIds) {
          hasError = true
          errors.bankDetailsMerchantRules = "Enter at least one MCC or merchant id for restricted merchant rules"
        }
      }
      break

    default:
      // No bank details validation needed for other processors
      break
  }

  return hasError
}

// ICICI Bank Details Section (Direct, Indirect, Pilot)
export const ICICIBankDetailsSection = ({
  formData,
  formErrors,
  setFormData,
  setFormErrors,
  showAccessKey = false,
  setShowAccessKey,
  decryptedAccessKey = "",
  mccList = [],
  purposeCodeList = [],
  fieldPrefix = "",
  handlePurposeCodeChange
}: {
  formData: any
  formErrors: Record<string, string>
  setFormData: (data: any) => void
  setFormErrors: (errors: any) => void
  showAccessKey?: boolean
  setShowAccessKey?: ((show: boolean) => void) | (() => Promise<void>)
  decryptedAccessKey?: string
  mccList?: any[]
  purposeCodeList?: any[]
  fieldPrefix?: string
  handlePurposeCodeChange?: (purposeCode: string) => void
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">


      {/* Merchant ID */}
      <div className="space-y-2">
        <label htmlFor={`${fieldPrefix}MerchantId`} className="block text-sm font-medium text-gray-700">
          Merchant ID
        </label>
        <input
          type="text"
          id={`${fieldPrefix}MerchantId`}
          placeholder="Enter merchant ID"
          value={formData.merchantId}
          onChange={e => {
            const { merchantId, ...rest } = formErrors
            setFormErrors(rest)
            setFormData({ ...formData, merchantId: e.target.value })
          }}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none ${formErrors.merchantId ? 'border-red-500' : 'border-gray-300'
            }`}
        />
        {formErrors.merchantId && (
          <p className="mt-1 text-sm text-red-600">{formErrors.merchantId}</p>
        )}
      </div>

      {/* Sub Merchant ID */}
      <div className="space-y-2">
        <label htmlFor={`${fieldPrefix}SubMerchantId`} className="block text-sm font-medium text-gray-700">
          Sub Merchant ID
        </label>
        <input
          type="text"
          id={`${fieldPrefix}SubMerchantId`}
          placeholder="Enter sub merchant ID"
          value={formData.subMerchantId}
          onChange={e => {
            const { subMerchantId, ...rest } = formErrors
            setFormErrors(rest)
            setFormData({ ...formData, subMerchantId: e.target.value })
          }}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none ${formErrors.subMerchantId ? 'border-red-500' : 'border-gray-300'
            }`}
        />
        {formErrors.subMerchantId && (
          <p className="mt-1 text-sm text-red-600">{formErrors.subMerchantId}</p>
        )}
      </div>

      {/* Redemption Type */}
      <div className="space-y-2">
        <label htmlFor={`${fieldPrefix}RedemptionType`} className="block text-sm font-medium text-gray-700">
          Redemption Type
        </label>
        <div className="relative">
          <select
            id={`${fieldPrefix}RedemptionType`}
            value={formData.redemptionType}
            onChange={e => {
              const { redemptionType, ...rest } = formErrors
              setFormErrors(rest)
              setFormData({ ...formData, redemptionType: e.target.value })
            }}
            className={`block w-full px-3 py-2 border ${formErrors.redemptionType ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none appearance-none transition`}
          >
            <option value="">Select redemption type</option>
            <option value="single">Single</option>
            <option value="multiple">Multiple</option>
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
        {formErrors.redemptionType && (
          <p className="mt-1 text-sm text-red-600">{formErrors.redemptionType}</p>
        )}
      </div>

      {/* Period */}
      <div className="space-y-2">
        <label htmlFor={`${fieldPrefix}Period`} className="block text-sm font-medium text-gray-700">
          Period
        </label>
        <input
          type="number"
          id={`${fieldPrefix}Period`}
          placeholder="Enter period"
          value={formData.period}
          onChange={e => {
            const { period, ...rest } = formErrors
            setFormErrors(rest)
            setFormData({ ...formData, period: parseInt(e.target.value) })
          }}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none ${formErrors.period ? 'border-red-500' : 'border-gray-300'
            }`}
        />
        {formErrors.period && (
          <p className="mt-1 text-sm text-red-600">{formErrors.period}</p>
        )}
      </div>


      {/* Purpose Code */}
      <div className="space-y-2">
        <label htmlFor={`${fieldPrefix}PurposeCode`} className="block text-sm font-medium text-gray-700">
          Purpose Code
        </label>
        <div className="relative">
          <select
            id={`${fieldPrefix}PurposeCode`}
            value={formData.bankDetails.purposeCode}
            onChange={e => {
              if (handlePurposeCodeChange) {
                handlePurposeCodeChange(e.target.value)
              } else {
                const { bankDetailsPurposeCode, ...rest } = formErrors
                setFormErrors(rest)
                setFormData({
                  ...formData,
                  bankDetails: { ...formData.bankDetails, purposeCode: e.target.value },
                })
              }
            }}
            className={`block w-full px-3 py-2 border ${formErrors.bankDetailsPurposeCode ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none appearance-none transition`}
          >
            <option value="">Select purpose code</option>
            {purposeCodeList.map(pc => (
              <option key={pc.purposeCode} value={pc.purposeCode}>
                {pc.purposeCode} - {pc.description}
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
        {formErrors.bankDetailsPurposeCode && (
          <p className="mt-1 text-sm text-red-600">{formErrors.bankDetailsPurposeCode}</p>
        )}
      </div>

      {/* MCC */}
      <div className="space-y-2">
        <label htmlFor={`${fieldPrefix}Mcc`} className="block text-sm font-medium text-gray-700">
          MCC
        </label>
        <div className="relative">
          <select
            id={`${fieldPrefix}Mcc`}
            value={formData.bankDetails.mcc}
            onChange={e => {
              const { bankDetailsMcc, ...rest } = formErrors
              setFormErrors(rest)
              setFormData({
                ...formData,
                bankDetails: { ...formData.bankDetails, mcc: e.target.value },
              })
            }}
            className={`block w-full px-3 py-2 border ${formErrors.bankDetailsMcc ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none appearance-none transition`}
          >
            <option value="">Select MCC</option>
            {mccList.map(mcc => (
              <option key={mcc.mcc} value={mcc.mcc}>
                {mcc.mcc} - {mcc.description}
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
        {formErrors.bankDetailsMcc && (
          <p className="mt-1 text-sm text-red-600">{formErrors.bankDetailsMcc}</p>
        )}
      </div>


      {/* Payer VA */}
      <div className="space-y-2">
        <label htmlFor={`${fieldPrefix}PayerVa`} className="block text-sm font-medium text-gray-700">
          Payer VA
        </label>
        <input
          type="text"
          id={`${fieldPrefix}PayerVa`}
          placeholder="Enter payer VA"
          value={formData.bankDetails.payerVa}
          onChange={e => {
            const { bankDetailsPayerVa, ...rest } = formErrors
            setFormErrors(rest)
            setFormData({
              ...formData,
              bankDetails: { ...formData.bankDetails, payerVa: e.target.value },
            })
          }}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none ${formErrors.bankDetailsPayerVa ? 'border-red-500' : 'border-gray-300'
            }`}
        />
        {formErrors.bankDetailsPayerVa && (
          <p className="mt-1 text-sm text-red-600">{formErrors.bankDetailsPayerVa}</p>
        )}
      </div>


      {/* Mandate Type */}
      <div className="space-y-2">
        <label htmlFor={`${fieldPrefix}MandateType`} className="block text-sm font-medium text-gray-700">
          Mandate Type
        </label>
        <div className="relative">
          <select
            id={`${fieldPrefix}MandateType`}
            value={formData.bankDetails.mandateType}
            onChange={e => {
              const { bankDetailsMandateType, bankDetailsPayeeVpa, ...rest } = formErrors
              setFormErrors(rest)
              setFormData({
                ...formData,
                bankDetails: {
                  ...formData.bankDetails,
                  mandateType: e.target.value,
                  payeeVpa: e.target.value === "04" ? formData.bankDetails.payeeVpa : ""
                },
              })
            }}
            className={`block w-full px-3 py-2 border ${formErrors.bankDetailsMandateType ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none appearance-none transition`}
          >
            <option value="">Select mandate type</option>
            <option value="01">Open Loop</option>
            <option value="02">Semi Closed Loop</option>
            <option value="04">Closed Loop</option>
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
        {formErrors.bankDetailsMandateType && (
          <p className="mt-1 text-sm text-red-600">{formErrors.bankDetailsMandateType}</p>
        )}
      </div>

      {/* Payee VPA - Only show for Closed Loop */}
      {formData.bankDetails.mandateType === "04" && (
        <div className="space-y-2">
          <label htmlFor={`${fieldPrefix}PayeeVpa`} className="block text-sm font-medium text-gray-700">
            Payee VPA
          </label>
          <input
            type="text"
            id={`${fieldPrefix}PayeeVpa`}
            placeholder="Enter payee VPA"
            value={formData.bankDetails.payeeVpa}
            onChange={e => {
              const { bankDetailsPayeeVpa, ...rest } = formErrors
              setFormErrors(rest)
              setFormData({
                ...formData,
                bankDetails: { ...formData.bankDetails, payeeVpa: e.target.value },
              })
            }}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none ${formErrors.bankDetailsPayeeVpa ? 'border-red-500' : 'border-gray-300'
              }`}
          />
          {formErrors.bankDetailsPayeeVpa && (
            <p className="mt-1 text-sm text-red-600">{formErrors.bankDetailsPayeeVpa}</p>
          )}
        </div>
      )}

      {/* Access Key */}
      <div className="space-y-2">
        <label htmlFor={`${fieldPrefix}AccessKey`} className="block text-sm font-medium text-gray-700">
          Access Key
        </label>
        <div className="flex gap-2">
          <input
            type={showAccessKey ? "text" : "password"}
            id={`${fieldPrefix}AccessKey`}
            placeholder="Enter access key"
            value={showAccessKey && decryptedAccessKey ? decryptedAccessKey : formData.bankDetails.accessKey}
            onChange={e => {
              const { bankDetailsAccessKey, ...rest } = formErrors
              setFormErrors(rest)
              setFormData({
                ...formData,
                bankDetails: { ...formData.bankDetails, accessKey: e.target.value },
              })
            }}
            readOnly={showAccessKey && !!decryptedAccessKey}
            className={`flex-1 px-3 py-2 border rounded-md focus:outline-none ${formErrors.bankDetailsAccessKey ? 'border-red-500' : 'border-gray-300'
              } ${showAccessKey && decryptedAccessKey ? 'bg-gray-50' : ''}`}
          />
          <button
            type="button"
            onClick={() => {
              if (setShowAccessKey) {
                try {
                  // Try calling as async function first (no parameters)
                  const result = (setShowAccessKey as () => Promise<void>)()
                  // Handle promise if it's async
                  if (result instanceof Promise) {
                    result.catch(console.error)
                  }
                } catch {
                  // If that fails, call as sync function with boolean parameter
                  try {
                    (setShowAccessKey as (show: boolean) => void)(!showAccessKey)
                  } catch (error) {
                    console.error('Error toggling access key visibility:', error)
                  }
                }
              }
            }}
            className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none"
          >
            {showAccessKey ? "Hide" : "Show"}
          </button>
        </div>
        {formErrors.bankDetailsAccessKey && (
          <p className="mt-1 text-sm text-red-600">{formErrors.bankDetailsAccessKey}</p>
        )}
      </div>
    </div>
  )
}

// YES Bank Details Section
export const YESBankDetailsSection = ({
  formData,
  formErrors,
  setFormData,
  setFormErrors,
  purposeCodeList = [],
  fieldPrefix = "",
  handlePurposeCodeChange
}: {
  formData: any
  formErrors: Record<string, string>
  setFormData: (data: any) => void
  setFormErrors: (errors: any) => void
  purposeCodeList?: any[]
  fieldPrefix?: string
  handlePurposeCodeChange?: (purposeCode: string) => void
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">




      {/* Period */}
      <div className="space-y-2">
        <label htmlFor={`${fieldPrefix}Period`} className="block text-sm font-medium text-gray-700">
          Period
        </label>
        <input
          type="number"
          id={`${fieldPrefix}Period`}
          placeholder="Enter period"
          value={formData.period}
          onChange={e => {
            const { period, ...rest } = formErrors
            setFormErrors(rest)
            setFormData({ ...formData, period: parseInt(e.target.value) })
          }}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none ${formErrors.period ? 'border-red-500' : 'border-gray-300'
            }`}
        />
        {formErrors.period && (
          <p className="mt-1 text-sm text-red-600">{formErrors.period}</p>
        )}
      </div>



      {/* Client Identifier (optional) */}
      <div className="space-y-2">
        <label htmlFor={`${fieldPrefix}ClientIdentifier`} className="block text-sm font-medium text-gray-700">
          Client Identifier
        </label>
        <input
          type="text"
          id={`${fieldPrefix}ClientIdentifier`}
          placeholder="Enter client identifier"
          value={formData.bankDetails.clientIdentifier || ""}
          onChange={e => {
            const { bankDetailsClientIdentifier, ...rest } = formErrors
            setFormErrors(rest)
            setFormData({
              ...formData,
              bankDetails: { ...formData.bankDetails, clientIdentifier: e.target.value },
            })
          }}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none ${formErrors.bankDetailsClientIdentifier ? 'border-red-500' : 'border-gray-300'
            }`}
        />
        {formErrors.bankDetailsClientIdentifier && (
          <p className="mt-1 text-sm text-red-600">{formErrors.bankDetailsClientIdentifier}</p>
        )}
      </div>

      {/* Purpose Code */}
      <div className="space-y-2">
        <label htmlFor={`${fieldPrefix}PurposeCode`} className="block text-sm font-medium text-gray-700">
          Purpose Code
        </label>
        <div className="relative">
          <select
            id={`${fieldPrefix}PurposeCode`}
            value={formData.bankDetails.purposeCode}
            onChange={e => {
              if (handlePurposeCodeChange) {
                handlePurposeCodeChange(e.target.value)
              } else {
                const { bankDetailsPurposeCode, ...rest } = formErrors
                setFormErrors(rest)
                setFormData({
                  ...formData,
                  bankDetails: { ...formData.bankDetails, purposeCode: e.target.value },
                })
              }
            }}
            className={`block w-full px-3 py-2 border ${formErrors.bankDetailsPurposeCode ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none appearance-none transition`}
          >
            <option value="">Select purpose code</option>
            {purposeCodeList.map(pc => (
              <option key={pc.purposeCode} value={pc.purposeCode}>
                {pc.purposeCode} - {pc.description}
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
        {formErrors.bankDetailsPurposeCode && (
          <p className="mt-1 text-sm text-red-600">{formErrors.bankDetailsPurposeCode}</p>
        )}
      </div>

      {/* Mandate Name */}
      <div className="space-y-2">
        <label htmlFor={`${fieldPrefix}MandateName`} className="block text-sm font-medium text-gray-700">
          Mandate Name
        </label>
        <input
          type="text"
          id={`${fieldPrefix}MandateName`}
          maxLength={50}
          placeholder="Enter mandate name"
          value={formData.bankDetails.mandateName || ""}
          onChange={e => {
            const { bankDetailsMandateName, ...rest } = formErrors
            setFormErrors(rest)
            setFormData({
              ...formData,
              bankDetails: { ...formData.bankDetails, mandateName: e.target.value },
            })
          }}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none ${formErrors.bankDetailsMandateName ? 'border-red-500' : 'border-gray-300'
            }`}
        />
        {formErrors.bankDetailsMandateName && (
          <p className="mt-1 text-sm text-red-600">{formErrors.bankDetailsMandateName}</p>
        )}
      </div>

      {/* Amount Rule */}
      <div className="space-y-2">
        <label htmlFor={`${fieldPrefix}AmountRule`} className="block text-sm font-medium text-gray-700">
          Amount Rule
        </label>
        <select
          id={`${fieldPrefix}AmountRule`}
          value={(formData.bankDetails.amountRule as any) || ""}
          onChange={e => {
            const { bankDetailsAmountRule, ...rest } = formErrors
            setFormErrors(rest)
            setFormData({
              ...formData,
              bankDetails: { ...formData.bankDetails, amountRule: e.target.value as any },
            })
          }}
          className={`block w-full px-3 py-2 border ${formErrors.bankDetailsAmountRule ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none appearance-none transition`}
        >
          <option value="">Select amount rule</option>
          <option value="MAX">MAX</option>
          <option value="EXACT">EXACT</option>
        </select>
        {formErrors.bankDetailsAmountRule && (
          <p className="mt-1 text-sm text-red-600">{formErrors.bankDetailsAmountRule}</p>
        )}
      </div>

      {/* Voucher Category */}
      <div className="space-y-2">
        <label htmlFor={`${fieldPrefix}VoucherCategory`} className="block text-sm font-medium text-gray-700">
          Voucher Category
        </label>
        <select
          id={`${fieldPrefix}VoucherCategory`}
          value={(formData.bankDetails.voucherCategory as any) || ""}
          onChange={e => {
            const { bankDetailsVoucherCategory, ...rest } = formErrors
            setFormErrors(rest)
            setFormData({
              ...formData,
              bankDetails: { ...formData.bankDetails, voucherCategory: e.target.value as any },
            })
          }}
          className={`block w-full px-3 py-2 border ${formErrors.bankDetailsVoucherCategory ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none appearance-none transition`}
        >
          <option value="">Select voucher category</option>
          <option value="CORPORATE">CORPORATE</option>
          <option value="GOVT">GOVT</option>
        </select>
        {formErrors.bankDetailsVoucherCategory && (
          <p className="mt-1 text-sm text-red-600">{formErrors.bankDetailsVoucherCategory}</p>
        )}
      </div>

      {/* Recurrence Pattern */}
      <div className="space-y-2">
        <label htmlFor={`${fieldPrefix}RecurrencePattern`} className="block text-sm font-medium text-gray-700">
          Recurrence Pattern
        </label>
        <select
          id={`${fieldPrefix}RecurrencePattern`}
          value={formData.bankDetails.recurrencePattern || ""}
          onChange={e => {
            const { bankDetailsRecurrencePattern, ...rest } = formErrors
            setFormErrors(rest)
            setFormData({
              ...formData,
              bankDetails: { ...formData.bankDetails, recurrencePattern: e.target.value as any },
            })
          }}
          className={`block w-full px-3 py-2 border ${formErrors.bankDetailsRecurrencePattern ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none appearance-none transition`}
        >
          <option value="">Select recurrence pattern</option>
          <option value="ONETIME">ONETIME</option>
          <option value="ASPRESENTED">ASPRESENTED</option>
          <option value="RELOADABLE">RELOADABLE</option>
        </select>
        {formErrors.bankDetailsRecurrencePattern && (
          <p className="mt-1 text-sm text-red-600">{formErrors.bankDetailsRecurrencePattern}</p>
        )}
      </div>

      {/* Payer Revocable */}
      <div className="space-y-2">
        <label htmlFor={`${fieldPrefix}PayerRevocable`} className="block text-sm font-medium text-gray-700">
          Payer Revocable
        </label>
        <select
          id={`${fieldPrefix}PayerRevocable`}
          value={formData.bankDetails.payerRevocable || ""}
          onChange={e => {
            const { bankDetailsPayerRevocable, ...rest } = formErrors
            setFormErrors(rest)
            setFormData({
              ...formData,
              bankDetails: { ...formData.bankDetails, payerRevocable: e.target.value },
            })
          }}
          className={`block w-full px-3 py-2 border ${formErrors.bankDetailsPayerRevocable ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none appearance-none transition`}
        >
          <option value="">Select</option>
          <option value="TRUE">TRUE</option>
          <option value="FALSE">FALSE</option>
        </select>
        {formErrors.bankDetailsPayerRevocable && (
          <p className="mt-1 text-sm text-red-600">{formErrors.bankDetailsPayerRevocable}</p>
        )}
      </div>

      {/* Pre Auth */}
      <div className="space-y-2">
        <label htmlFor={`${fieldPrefix}PreAuth`} className="block text-sm font-medium text-gray-700">
          Pre Auth
        </label>
        <select
          id={`${fieldPrefix}PreAuth`}
          value={formData.bankDetails.preAuth === undefined ? "" : String(formData.bankDetails.preAuth)}
          onChange={e => {
            const { bankDetailsPreAuth, ...rest } = formErrors
            setFormErrors(rest)
            setFormData({
              ...formData,
              bankDetails: { ...formData.bankDetails, preAuth: e.target.value === "true" },
            })
          }}
          className={`block w-full px-3 py-2 border ${formErrors.bankDetailsPreAuth ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none appearance-none transition`}
        >
          <option value="">Select</option>
          <option value="true">Yes (PRE)</option>
          <option value="false">No (GPV)</option>
        </select>
        {formErrors.bankDetailsPreAuth && (
          <p className="mt-1 text-sm text-red-600">{formErrors.bankDetailsPreAuth}</p>
        )}
      </div>

      {/* Ref URL (optional) */}
      <div className="space-y-2 md:col-span-2">
        <label htmlFor={`${fieldPrefix}Refurl`} className="block text-sm font-medium text-gray-700">
          Ref URL <span className="text-gray-400 font-normal">(optional)</span>
        </label>
        <input
          type="url"
          id={`${fieldPrefix}Refurl`}
          placeholder="https://example.com"
          value={formData.bankDetails.refurl || ""}
          onChange={e => {
            const { bankDetailsRefurl, ...rest } = formErrors
            setFormErrors(rest)
            setFormData({
              ...formData,
              bankDetails: { ...formData.bankDetails, refurl: e.target.value || undefined },
            })
          }}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none ${formErrors.bankDetailsRefurl ? 'border-red-500' : 'border-gray-300'}`}
        />
        {formErrors.bankDetailsRefurl && (
          <p className="mt-1 text-sm text-red-600">{formErrors.bankDetailsRefurl}</p>
        )}
      </div>
    </div>
  )
}

// Hook: closes an open dropdown when the user clicks outside of it.
const useCloseOnOutsideClick = (onClose: () => void) => {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        onClose()
      }
    }
    document.addEventListener("mousedown", handleClick)
    return () => document.removeEventListener("mousedown", handleClick)
  }, [onClose])

  return ref
}

const emptyIciciCbdcLocationRow = () => ({ state: "", city: "", bufferZone: "" })

// ICICI CBDC Bank Details Section.
//
// These fields (sponsor-details, purpose, mcc, language, and repeatable
// {state, city, bufferZone} location rows) are stored in the
// program's bankDetails exactly like any other processor's bank details -
// program creation/update no longer calls any ICICI CBDC bank API (that
// coupling, and the ruleId/authBy fields it introduced, has been removed;
// rule management is a separate concern, decoupled from programs).
//
// No validation is applied to any of these fields yet (see
// validateBankDetails's ICICI_CBDC case) - exact validation rules haven't
// been provided, so submission is intentionally permissive for now.
export const ICICICBDCBankDetailsSection = ({
  formData,
  formErrors,
  setFormData,
  setFormErrors,
  purposeCodeOptions = [],
  fieldPrefix = "",
  onExpandPurposeCode,
  loadingMccsForPurposeCode = "",
}: {
  formData: any
  formErrors: Record<string, string>
  setFormData: (data: any) => void
  setFormErrors: (errors: any) => void
  purposeCodeOptions?: PurposeCodeOption[]
  fieldPrefix?: string
  onExpandPurposeCode?: (code: string) => void
  loadingMccsForPurposeCode?: string
}) => {
  const [expandedPurposeCodes, setExpandedPurposeCodes] = useState<Record<string, boolean>>({})
  const [mccDropdownOpen, setMccDropdownOpen] = useState(false)
  const mccDropdownRef = useCloseOnOutsideClick(() => setMccDropdownOpen(false))

  const mccString: string = formData.bankDetails?.mcc || ""
  const selectedMccs: string[] = mccString ? mccString.split(",").map((m: string) => m.trim()).filter(Boolean) : []
  const locationRows: Array<{ state: string; city: string; bufferZone: string }> =
    formData.bankDetails?.location || []

  const setSelectedMccs = (mccs: string[]) => {
    setFormData({
      ...formData,
      bankDetails: { ...formData.bankDetails, mcc: mccs.join(", ") },
    })
  }

  const setLocationRows = (rows: Array<{ state: string; city: string; bufferZone: string }>) => {
    setFormData({
      ...formData,
      bankDetails: { ...formData.bankDetails, location: rows },
    })
  }

  const updateLocationRow = (index: number, updates: Partial<{ state: string; city: string; bufferZone: string }>) => {
    const next = locationRows.map((row, i) => (i === index ? { ...row, ...updates } : row))
    setLocationRows(next)
  }

  const removeLocationRow = (index: number) => {
    setLocationRows(locationRows.filter((_, i) => i !== index))
  }

  const togglePurposeCodeExpanded = (pc: PurposeCodeOption) => {
    const isExpanded = !!expandedPurposeCodes[pc.code]
    setExpandedPurposeCodes({ ...expandedPurposeCodes, [pc.code]: !isExpanded })

    // Lazily fetch this purpose code's MCCs the first time it's expanded,
    // since the list endpoint doesn't include them.
    if (!isExpanded && getMccsForPurposeCode(pc).length === 0 && onExpandPurposeCode) {
      onExpandPurposeCode(pc.code)
    }
  }

  return (
    <div className="grid grid-cols-1 gap-6">

      {/* Sponsor Details */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Sponsor Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label htmlFor={`${fieldPrefix}SponsorId`} className="block text-sm font-medium text-gray-700">
              Sponsor ID
            </label>
            <input
              type="text"
              id={`${fieldPrefix}SponsorId`}
              placeholder="Enter sponsor ID"
              value={formData.bankDetails?.sponsorId || ""}
              onChange={e => {
                const { bankDetailsSponsorId, ...rest } = formErrors
                setFormErrors(rest)
                setFormData({
                  ...formData,
                  bankDetails: { ...formData.bankDetails, sponsorId: e.target.value },
                })
              }}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none ${formErrors.bankDetailsSponsorId ? 'border-red-500' : 'border-gray-300'
                }`}
            />
            {formErrors.bankDetailsSponsorId && (
              <p className="mt-1 text-sm text-red-600">{formErrors.bankDetailsSponsorId}</p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor={`${fieldPrefix}SponsorName`} className="block text-sm font-medium text-gray-700">
              Sponsor Name
            </label>
            <input
              type="text"
              id={`${fieldPrefix}SponsorName`}
              placeholder="Enter sponsor name"
              value={formData.bankDetails?.sponsorName || ""}
              onChange={e => {
                const { bankDetailsSponsorName, ...rest } = formErrors
                setFormErrors(rest)
                setFormData({
                  ...formData,
                  bankDetails: { ...formData.bankDetails, sponsorName: e.target.value },
                })
              }}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none ${formErrors.bankDetailsSponsorName ? 'border-red-500' : 'border-gray-300'
                }`}
            />
            {formErrors.bankDetailsSponsorName && (
              <p className="mt-1 text-sm text-red-600">{formErrors.bankDetailsSponsorName}</p>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

        {/* Purpose */}
        <div className="space-y-2">
          <label htmlFor={`${fieldPrefix}Purpose`} className="block text-sm font-medium text-gray-700">
            Purpose
          </label>
          <input
            type="text"
            id={`${fieldPrefix}Purpose`}
            placeholder="Enter purpose"
            value={formData.bankDetails?.purpose || ""}
            onChange={e => {
              const { bankDetailsPurpose, ...rest } = formErrors
              setFormErrors(rest)
              setFormData({
                ...formData,
                bankDetails: { ...formData.bankDetails, purpose: e.target.value },
              })
            }}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none ${formErrors.bankDetailsPurpose ? 'border-red-500' : 'border-gray-300'
              }`}
          />
          {formErrors.bankDetailsPurpose && (
            <p className="mt-1 text-sm text-red-600">{formErrors.bankDetailsPurpose}</p>
          )}
        </div>

        {/* Period */}
        <div className="space-y-2">
          <label htmlFor={`${fieldPrefix}Period`} className="block text-sm font-medium text-gray-700">
            Period
          </label>
          <input
            type="number"
            id={`${fieldPrefix}Period`}
            placeholder="Enter period"
            value={formData.period}
            onChange={e => {
              const { period, ...rest } = formErrors
              setFormErrors(rest)
              setFormData({ ...formData, period: parseInt(e.target.value) })
            }}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none ${formErrors.period ? 'border-red-500' : 'border-gray-300'
              }`}
          />
          {formErrors.period && (
            <p className="mt-1 text-sm text-red-600">{formErrors.period}</p>
          )}
        </div>
      </div>

      {/* MCC (dropdown of Purpose Codes, each expandable into its MCCs) */}
      <div className="space-y-2" ref={mccDropdownRef}>
        <label htmlFor={`${fieldPrefix}MccDropdownButton`} className="block text-sm font-medium text-gray-700">
          MCC
        </label>
        <div className="relative">
          <button
            type="button"
            id={`${fieldPrefix}MccDropdownButton`}
            onClick={() => setMccDropdownOpen(!mccDropdownOpen)}
            className={`w-full flex items-center justify-between px-3 py-2 border rounded-md bg-white text-sm text-left focus:outline-none ${formErrors.bankDetailsMcc ? 'border-red-500' : 'border-gray-300'
              }`}
          >
            <span className={selectedMccs.length > 0 ? "text-gray-700" : "text-gray-400"}>
              {selectedMccs.length > 0
                ? `${selectedMccs.length} MCC(s) selected: ${selectedMccs.join(', ')}`
                : "Select MCC(s) via purpose code"}
            </span>
            <svg className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${mccDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {mccDropdownOpen && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-80 overflow-y-auto divide-y">
              {purposeCodeOptions.length === 0 && (
                <p className="p-3 text-sm text-gray-400">No purpose codes available</p>
              )}
              {purposeCodeOptions.map(pc => {
                const isExpanded = !!expandedPurposeCodes[pc.code]
                const allSelected = areAllMccsInPurposeCodeSelected(selectedMccs, pc)
                const mccsInCode = getMccsForPurposeCode(pc)
                const isLoadingThisCode = loadingMccsForPurposeCode === pc.code

                return (
                  <div key={pc.code} className="p-2">
                    <div className="flex items-center justify-between gap-2">
                      <button
                        type="button"
                        id={`${fieldPrefix}PurposeCodeToggle-${pc.code}`}
                        onClick={() => togglePurposeCodeExpanded(pc)}
                        className="flex items-center gap-2 text-sm text-gray-700 flex-1 text-left px-1 py-1 rounded hover:bg-gray-50"
                      >
                        <svg
                          className={`h-4 w-4 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                          fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                        <span>{pc.code} - {pc.description}</span>
                      </button>
                      <label className="flex items-center gap-1 text-xs text-gray-600 whitespace-nowrap px-1">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={() => {
                            const { bankDetailsMcc, ...rest } = formErrors
                            setFormErrors(rest)
                            setSelectedMccs(
                              allSelected
                                ? deselectAllMccsInPurposeCode(selectedMccs, pc)
                                : selectAllMccsInPurposeCode(selectedMccs, pc)
                            )
                          }}
                        />
                        Select all
                      </label>
                    </div>

                    {isExpanded && (
                      <div className="mt-1 pl-6 pr-1 space-y-1">
                        {isLoadingThisCode && (
                          <p className="text-xs text-gray-400 py-1">Loading MCCs...</p>
                        )}
                        {!isLoadingThisCode && mccsInCode.length === 0 && (
                          <p className="text-xs text-gray-400 py-1">No MCCs mapped to this purpose code</p>
                        )}
                        {!isLoadingThisCode && mccsInCode.map(mcc => (
                          <label key={mcc} className="flex items-center gap-2 text-sm text-gray-600 py-0.5">
                            <input
                              type="checkbox"
                              checked={selectedMccs.includes(mcc)}
                              onChange={() => {
                                const { bankDetailsMcc, ...rest } = formErrors
                                setFormErrors(rest)
                                setSelectedMccs(toggleMcc(selectedMccs, mcc))
                              }}
                            />
                            {mcc} - {pc.mccs[mcc]}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
        <p className="text-xs text-gray-500">
          Expand a purpose code to pick individual MCCs, or check "select all" to add every MCC within it.
          The final MCC list can combine MCCs from any number of purpose codes.
        </p>
        {formErrors.bankDetailsMcc && (
          <p className="mt-1 text-sm text-red-600">{formErrors.bankDetailsMcc}</p>
        )}
      </div>

      {/* Location (optional, repeatable {state, city, bufferZone} rows) */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-gray-700">
            Location <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <button
            type="button"
            id={`${fieldPrefix}AddLocationRow`}
            onClick={() => setLocationRows([...locationRows, emptyIciciCbdcLocationRow()])}
            className="text-sm font-medium text-brand hover:underline"
          >
            + Add location
          </button>
        </div>

        {locationRows.length === 0 && (
          <p className="text-xs text-gray-400">No location restriction added - tokens can be used anywhere.</p>
        )}

        {locationRows.map((row, index) => {
          const citiesForRowState = getCitiesForState(row.state)
          return (
            <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end border rounded-md p-3">
              <div className="space-y-1">
                <label htmlFor={`${fieldPrefix}LocationState-${index}`} className="block text-xs font-medium text-gray-500">
                  State
                </label>
                <select
                  id={`${fieldPrefix}LocationState-${index}`}
                  value={row.state}
                  onChange={e => updateLocationRow(index, { state: e.target.value, city: "" })}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 focus:outline-none appearance-none transition"
                >
                  <option value="">Select state</option>
                  {LOCATION_STATES.map(s => (
                    <option key={s.name} value={s.name}>{s.name}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <label htmlFor={`${fieldPrefix}LocationCity-${index}`} className="block text-xs font-medium text-gray-500">
                  City
                </label>
                <select
                  id={`${fieldPrefix}LocationCity-${index}`}
                  value={row.city}
                  disabled={!row.state}
                  onChange={e => updateLocationRow(index, { city: e.target.value })}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 focus:outline-none appearance-none transition disabled:bg-gray-50 disabled:text-gray-400"
                >
                  <option value="">Select city</option>
                  {citiesForRowState.map(c => (
                    <option key={c.name} value={c.name}>{c.name}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <label htmlFor={`${fieldPrefix}LocationBufferZone-${index}`} className="block text-xs font-medium text-gray-500">
                  Buffer Zone
                </label>
                <input
                  type="number"
                  inputMode="decimal"
                  min={0}
                  id={`${fieldPrefix}LocationBufferZone-${index}`}
                  placeholder="Buffer zone"
                  value={row.bufferZone}
                  onChange={e => updateLocationRow(index, { bufferZone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none"
                />
              </div>

              <div>
                <button
                  type="button"
                  id={`${fieldPrefix}RemoveLocationRow-${index}`}
                  onClick={() => removeLocationRow(index)}
                  className="w-full px-3 py-2 text-sm font-medium text-red-600 bg-white border border-gray-300 rounded-md hover:bg-red-50 focus:outline-none"
                >
                  Remove
                </button>
              </div>
            </div>
          )
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

        {/* Language */}
        <div className="space-y-2">
          <label htmlFor={`${fieldPrefix}Language`} className="block text-sm font-medium text-gray-700">
            Language <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <select
            id={`${fieldPrefix}Language`}
            value={formData.bankDetails?.language || ""}
            onChange={e => {
              setFormData({
                ...formData,
                bankDetails: { ...formData.bankDetails, language: e.target.value },
              })
            }}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 focus:outline-none appearance-none transition"
          >
            <option value="">Select language</option>
            <option value="ENGLISH">English</option>
            <option value="HINDI">Hindi</option>
          </select>
        </div>
      </div>
    </div>
  )
}

// MYP (Malaysia DuitNow QR) program details. Stored in bankDetails like every
// other processor's brand-specific fields (see programs/src/services/
// program.service.js#provisionMypProgram, which reads these to build the
// POST /programs request).
export const MYPBankDetailsSection = ({
  formData,
  formErrors,
  setFormData,
  setFormErrors,
  fieldPrefix = "",
  // Only present in Edit mode (a program must exist before it can be
  // provisioned) - wired from EditPrograms.tsx to
  // programs/src/services/program.service.js#provisionMypForProgram via the
  // provisionMypForProgram thunk, so provisioning can be retried in place
  // (e.g. after fixing principal onboarding) without resubmitting the form.
  programId,
  onProvision,
  provisionLoading = false,
  provisionError = null,
}: {
  formData: any
  formErrors: Record<string, string>
  setFormData: (data: any) => void
  setFormErrors: (errors: any) => void
  fieldPrefix?: string
  programId?: string
  onProvision?: () => void
  provisionLoading?: boolean
  provisionError?: string | null
}) => {
  const bankDetails = formData.bankDetails || {}
  const expiry = bankDetails.expiry || { mode: 'DAYS_FROM_LOAD', days: '' }
  const limits = bankDetails.limits || {}
  const merchantRules = bankDetails.merchantRules || { mode: 'ALL' }

  // period (voucher validity in days) always follows the expiry; updated in the
  // same state change as the expiry itself.
  const updateBankDetails = (updates: Record<string, any>) => {
    const next: Record<string, any> = { ...formData, bankDetails: { ...bankDetails, ...updates } }
    if (updates.expiry) {
      const derived = derivePeriodFromMypExpiry(updates.expiry)
      if (derived != null) next.period = derived
    }
    setFormData(next)
  }

  const derivedPeriod = derivePeriodFromMypExpiry(expiry)

  // Existing programs load with their stored period; make sure it matches the expiry.
  useEffect(() => {
    if (derivedPeriod != null && formData.period !== derivedPeriod) {
      setFormData({ ...formData, period: derivedPeriod })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [derivedPeriod])

  const provisionStatus: string | undefined = bankDetails.mypProvisionStatus
  const provisionErrorMessage: string | undefined = bankDetails.mypProvisionError

  return (
    <>
      {/* Processor (MYP) provisioning status - only shown once the program
          exists (programId set, i.e. Edit mode). A brand-new program in
          AddPrograms.tsx is provisioned automatically right after creation,
          so there's nothing to show/retry here until it's been saved once. */}
      {programId && (
        <div className={`mb-6 rounded-lg border p-4 flex items-start justify-between gap-4 ${
          provisionStatus === 'provisioned' ? 'border-green-200 bg-green-50' :
          provisionStatus === 'failed' ? 'border-red-200 bg-red-50' :
          'border-gray-200 bg-gray-50'
        }`}>
          <div>
            <p className="text-sm font-medium text-gray-900">
              {provisionStatus === 'provisioned' && 'Provisioned with MYP'}
              {provisionStatus === 'failed' && 'Not provisioned with MYP'}
              {!provisionStatus && 'Not yet provisioned with MYP'}
            </p>
            {provisionStatus === 'provisioned' && bankDetails.mypProgramId && (
              <p className="text-xs text-gray-500 font-mono mt-1">MYP programId: {bankDetails.mypProgramId}</p>
            )}
            {(provisionErrorMessage || provisionError) && (
              <p className="text-xs text-red-600 mt-1">{provisionError || provisionErrorMessage}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onProvision}
            disabled={provisionLoading || !onProvision}
            className="px-3 py-1.5 text-sm font-medium text-white rounded-md bg-brand hover:bg-brand/80 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {provisionLoading ? 'Provisioning...' : provisionStatus === 'provisioned' ? 'Re-sync with MYP' : 'Provision with MYP'}
          </button>
        </div>
      )}

    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Expiry Mode */}
      <div className="space-y-2">
        <label htmlFor={`${fieldPrefix}ExpiryMode`} className="block text-sm font-medium text-gray-700">
          Expiry Mode
        </label>
        <select
          id={`${fieldPrefix}ExpiryMode`}
          value={expiry.mode || 'DAYS_FROM_LOAD'}
          onChange={e => {
            const { bankDetailsExpiryMode, ...rest } = formErrors
            setFormErrors(rest)
            updateBankDetails({ expiry: { mode: e.target.value } })
          }}
          className={`block w-full px-3 py-2 border ${formErrors.bankDetailsExpiryMode ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none appearance-none transition`}
        >
          <option value="DAYS_FROM_LOAD">Days from load</option>
          <option value="FIXED_DATE">Fixed date</option>
        </select>
        {formErrors.bankDetailsExpiryMode && (
          <p className="mt-1 text-sm text-red-600">{formErrors.bankDetailsExpiryMode}</p>
        )}
      </div>

      {expiry.mode === 'FIXED_DATE' ? (
        <div className="space-y-2">
          <label htmlFor={`${fieldPrefix}ExpiryDate`} className="block text-sm font-medium text-gray-700">
            Expiry Date
          </label>
          <input
            type="date"
            id={`${fieldPrefix}ExpiryDate`}
            value={expiry.date || ""}
            onChange={e => {
              const { bankDetailsExpiryDate, ...rest } = formErrors
              setFormErrors(rest)
              updateBankDetails({ expiry: { mode: 'FIXED_DATE', date: e.target.value } })
            }}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none ${formErrors.bankDetailsExpiryDate ? 'border-red-500' : 'border-gray-300'}`}
          />
          {formErrors.bankDetailsExpiryDate && (
            <p className="mt-1 text-sm text-red-600">{formErrors.bankDetailsExpiryDate}</p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <label htmlFor={`${fieldPrefix}ExpiryDays`} className="block text-sm font-medium text-gray-700">
            Days From Load
          </label>
          <input
            type="number"
            min={1}
            id={`${fieldPrefix}ExpiryDays`}
            placeholder="e.g. 90"
            value={expiry.days || ""}
            onChange={e => {
              const { bankDetailsExpiryDays, ...rest } = formErrors
              setFormErrors(rest)
              updateBankDetails({ expiry: { mode: 'DAYS_FROM_LOAD', days: e.target.value ? Number(e.target.value) : '' } })
            }}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none ${formErrors.bankDetailsExpiryDays ? 'border-red-500' : 'border-gray-300'}`}
          />
          {formErrors.bankDetailsExpiryDays && (
            <p className="mt-1 text-sm text-red-600">{formErrors.bankDetailsExpiryDays}</p>
          )}
        </div>
      )}

      {derivedPeriod != null && (
        <p className="md:col-span-2 text-sm text-gray-500">
          Voucher validity (period): {derivedPeriod} {derivedPeriod === 1 ? 'day' : 'days'} - set automatically from the expiry above
        </p>
      )}

      {/* Limits */}
      <div className="space-y-2">
        <label htmlFor={`${fieldPrefix}MinLoad`} className="block text-sm font-medium text-gray-700">
          Min Load <span className="text-gray-400 font-normal">(optional, default 1.00)</span>
        </label>
        <input
          type="number" min={0} step="0.01"
          id={`${fieldPrefix}MinLoad`}
          value={limits.minLoad || ""}
          onChange={e => updateBankDetails({ limits: { ...limits, minLoad: e.target.value } })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none"
        />
      </div>
      <div className="space-y-2">
        <label htmlFor={`${fieldPrefix}MaxLoad`} className="block text-sm font-medium text-gray-700">
          Max Load
        </label>
        <input
          type="number" min={0} step="0.01"
          id={`${fieldPrefix}MaxLoad`}
          value={limits.maxLoad || ""}
          onChange={e => {
            const { bankDetailsMaxLoad, ...rest } = formErrors
            setFormErrors(rest)
            updateBankDetails({ limits: { ...limits, maxLoad: e.target.value } })
          }}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none ${formErrors.bankDetailsMaxLoad ? 'border-red-500' : 'border-gray-300'}`}
        />
        {formErrors.bankDetailsMaxLoad && (
          <p className="mt-1 text-sm text-red-600">{formErrors.bankDetailsMaxLoad}</p>
        )}
      </div>
      <div className="space-y-2">
        <label htmlFor={`${fieldPrefix}MaxBalance`} className="block text-sm font-medium text-gray-700">
          Max Balance
        </label>
        <input
          type="number" min={0} step="0.01"
          id={`${fieldPrefix}MaxBalance`}
          value={limits.maxBalance || ""}
          onChange={e => {
            const { bankDetailsMaxBalance, ...rest } = formErrors
            setFormErrors(rest)
            updateBankDetails({ limits: { ...limits, maxBalance: e.target.value } })
          }}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none ${formErrors.bankDetailsMaxBalance ? 'border-red-500' : 'border-gray-300'}`}
        />
        {formErrors.bankDetailsMaxBalance && (
          <p className="mt-1 text-sm text-red-600">{formErrors.bankDetailsMaxBalance}</p>
        )}
      </div>
      <div className="space-y-2">
        <label htmlFor={`${fieldPrefix}PerTransaction`} className="block text-sm font-medium text-gray-700">
          Per Transaction Limit <span className="text-gray-400 font-normal">(optional)</span>
        </label>
        <input
          type="number" min={0} step="0.01"
          id={`${fieldPrefix}PerTransaction`}
          value={limits.perTransaction || ""}
          onChange={e => updateBankDetails({ limits: { ...limits, perTransaction: e.target.value } })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none"
        />
      </div>
      <div className="space-y-2">
        <label htmlFor={`${fieldPrefix}DailySpend`} className="block text-sm font-medium text-gray-700">
          Daily Spend Limit <span className="text-gray-400 font-normal">(optional)</span>
        </label>
        <input
          type="number" min={0} step="0.01"
          id={`${fieldPrefix}DailySpend`}
          value={limits.dailySpend || ""}
          onChange={e => updateBankDetails({ limits: { ...limits, dailySpend: e.target.value } })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none"
        />
      </div>
      <div className="space-y-2">
        <label htmlFor={`${fieldPrefix}LowFloatThreshold`} className="block text-sm font-medium text-gray-700">
          Low Float Alert Threshold <span className="text-gray-400 font-normal">(optional)</span>
        </label>
        <input
          type="number" min={0} step="0.01"
          id={`${fieldPrefix}LowFloatThreshold`}
          value={bankDetails.lowFloatThreshold || ""}
          onChange={e => updateBankDetails({ lowFloatThreshold: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none"
        />
      </div>

      {/* Merchant Rules */}
      <div className="space-y-2 md:col-span-2">
        <label htmlFor={`${fieldPrefix}MerchantRulesMode`} className="block text-sm font-medium text-gray-700">
          Merchant Rules
        </label>
        <select
          id={`${fieldPrefix}MerchantRulesMode`}
          value={merchantRules.mode || 'ALL'}
          onChange={e => updateBankDetails({ merchantRules: { ...merchantRules, mode: e.target.value } })}
          className="block w-full md:w-64 px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 focus:outline-none appearance-none transition"
        >
          <option value="ALL">All merchants</option>
          <option value="RESTRICTED">Restricted (MCC / merchant id allow-list)</option>
        </select>
      </div>

      {merchantRules.mode === 'RESTRICTED' && (
        <>
          <div className="space-y-2">
            <label htmlFor={`${fieldPrefix}MerchantRulesMccs`} className="block text-sm font-medium text-gray-700">
              Allowed MCCs <span className="text-gray-400 font-normal">(comma-separated)</span>
            </label>
            <input
              type="text"
              id={`${fieldPrefix}MerchantRulesMccs`}
              placeholder="5812, 5411"
              value={merchantRules.mccs || ""}
              onChange={e => {
                const { bankDetailsMerchantRules, ...rest } = formErrors
                setFormErrors(rest)
                updateBankDetails({ merchantRules: { ...merchantRules, mccs: e.target.value } })
              }}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none ${formErrors.bankDetailsMerchantRules ? 'border-red-500' : 'border-gray-300'}`}
            />
          </div>
          <div className="space-y-2">
            <label htmlFor={`${fieldPrefix}MerchantRulesIds`} className="block text-sm font-medium text-gray-700">
              Allowed Merchant IDs <span className="text-gray-400 font-normal">(comma-separated)</span>
            </label>
            <input
              type="text"
              id={`${fieldPrefix}MerchantRulesIds`}
              placeholder="000010000100004"
              value={merchantRules.merchantIds || ""}
              onChange={e => {
                const { bankDetailsMerchantRules, ...rest } = formErrors
                setFormErrors(rest)
                updateBankDetails({ merchantRules: { ...merchantRules, merchantIds: e.target.value } })
              }}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none ${formErrors.bankDetailsMerchantRules ? 'border-red-500' : 'border-gray-300'}`}
            />
          </div>
          {formErrors.bankDetailsMerchantRules && (
            <p className="mt-1 text-sm text-red-600 md:col-span-2">{formErrors.bankDetailsMerchantRules}</p>
          )}
        </>
      )}
    </div>
    </>
  )
}

// Render Bank Details Section based on processor type
export const renderBankDetailsSection = (processorType: string, props: {
  formData: any
  formErrors: Record<string, string>
  setFormData: (data: any) => void
  setFormErrors: (errors: any) => void
  showAccessKey?: boolean
  setShowAccessKey?: ((show: boolean) => void) | (() => Promise<void>)
  decryptedAccessKey?: string
  mccList?: any[]
  purposeCodeList?: any[]
  purposeCodeOptions?: PurposeCodeOption[]
  fieldPrefix?: string
  handlePurposeCodeChange?: (purposeCode: string) => void
  onExpandPurposeCode?: (code: string) => void
  loadingMccsForPurposeCode?: string
  // MYP only - see MYPBankDetailsSection
  programId?: string
  onProvision?: () => void
  provisionLoading?: boolean
  provisionError?: string | null
}) => {

  switch (getProcessorBrand(processorType)) {
    case 'ICICI':
      return (
        <>
          {/* Bank & Redemption Details */}
          <div className="mt-6">
            <ICICIBankDetailsSection {...props} />
          </div>
        </>
      )

    case 'YESBANK':
      return (
        <>
          {/* Bank & Redemption Details */}
          <div className="mt-6">
            <YESBankDetailsSection {...props} />
          </div>
        </>
      )

    case 'ICICI_CBDC':
      return (
        <>
          {/* Bank & Redemption Details */}
          <div className="mt-6">
            <ICICICBDCBankDetailsSection {...props} />
          </div>
        </>
      )

    case 'MYP':
      return (
        <>
          {/* MYP Program Details */}
          <div className="mt-6">
            <MYPBankDetailsSection {...props} />
          </div>
        </>
      )

    default:
      return null // No bank details section for other processors
  }
}
